import React, { useEffect, useState } from 'react';
import api from '../services/ApiService';

type Jail = { name: string; currentlyBanned: number; totalBanned: number; bannedIps: string[] };
type CsDecision = { ip: string; scenario: string; action: string; duration: string; country?: string; origin?: string };
type BansData = { generatedAt: string; totalBanned: number; jails: Jail[]; crowdsec: CsDecision[] };

export default function SecurityBans() {
  const [data, setData] = useState<BansData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    try { setData(await api.getSecurityBans()); setErr(''); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Failed to load bans'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const jails = data?.jails || [];
  const activeJails = jails.filter((j) => j.bannedIps.length > 0);
  const totalIps = jails.reduce((n, j) => n + j.bannedIps.length, 0);
  const cs = data?.crowdsec || [];

  return (
    <>
      <div className="admin-page-title">Banned IPs</div>
      <div className="admin-page-subtitle">
        Live IP bans across every fail2ban jail and CrowdSec — the platform's edge defence against SIP scanners,
        brute-force and abuse. Read-only view for super admins.
      </div>

      <div className="dashboard-grid">
        <StatCard title="Banned IPs (fail2ban)" value={totalIps} danger={totalIps > 0} />
        <StatCard title="Active Jails" value={activeJails.length} />
        <StatCard title="Total Jails" value={jails.length} />
        <StatCard title="CrowdSec Bans" value={cs.length} danger={cs.length > 0} />
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>fail2ban jails{data ? `  ·  updated ${new Date(data.generatedAt).toLocaleTimeString()}` : ''}</h3>
          <button className="secondary-btn" onClick={load}>Refresh</button>
        </div>
        {err && <p className="admin-muted" style={{ color: '#dc2626' }}>{err}</p>}
        {loading && !data && <p className="admin-muted">Loading…</p>}
        {data && jails.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr><th>Jail</th><th>Currently banned</th><th>Total (all-time)</th><th>Banned IPs</th></tr>
            </thead>
            <tbody>
              {jails.map((j) => (
                <tr key={j.name} style={j.bannedIps.length ? { background: '#fef2f2' } : undefined}>
                  <td><strong>{j.name}</strong></td>
                  <td>{j.currentlyBanned}</td>
                  <td>{j.totalBanned}</td>
                  <td>
                    {j.bannedIps.length
                      ? j.bannedIps.map((ip) => (
                          <span key={ip} className="admin-pill red" style={{ marginRight: 4, marginBottom: 4, display: 'inline-block' }}>{ip}</span>
                        ))
                      : <span className="admin-muted">none</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {cs.length > 0 && (
        <div className="content-card">
          <div className="admin-card-header"><h3>CrowdSec decisions</h3></div>
          <table className="admin-table">
            <thead>
              <tr><th>IP</th><th>Reason</th><th>Action</th><th>Country</th><th>Expires in</th></tr>
            </thead>
            <tbody>
              {cs.map((d, i) => (
                <tr key={d.ip + '-' + i}>
                  <td><span className="admin-pill red">{d.ip}</span></td>
                  <td>{d.scenario}</td>
                  <td>{d.action}</td>
                  <td>{d.country || '—'}</td>
                  <td className="admin-muted">{d.duration || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function StatCard({ title, value, danger }: { title: string; value: number; danger?: boolean }) {
  return (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value" style={danger ? { color: '#dc2626' } : undefined}>{value}</div>
    </div>
  );
}
